import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useActiveRole } from '@/hooks/useActiveRole';
import { UserRole } from '@features/authentication/types';
import type { InsightFilterOptions } from '../hooks/useInsight';
import type { DateRangePreset } from '../components/DateRangeFilter';

/**
 * Insight context value interface
 */
interface InsightContextValue {
  /** Currently selected tenant (SYSTEM_ADMIN only) */
  selectedTenantId: string | undefined;
  setSelectedTenantId: (tenantId: string | undefined) => void;

  /** Date range preset */
  dateRangePreset: DateRangePreset;
  setDateRangePreset: (preset: DateRangePreset) => void;

  /** Custom date range (when preset is 'custom') */
  customDateRange: { start: string; end: string } | undefined;
  setCustomDateRange: (range: { start: string; end: string } | undefined) => void;

  /** Time granularity */
  granularity: 'day' | 'week' | 'month';
  setGranularity: (granularity: 'day' | 'week' | 'month') => void;

  /** Computed insight filters */
  filters: InsightFilterOptions;

  /** Whether insight queries should be enabled */
  queryEnabled: boolean;

  /** Current user's active role */
  activeRole: ReturnType<typeof useActiveRole>;
}

const InsightContext = createContext<InsightContextValue | null>(null);

/**
 * Hook to access the insight context
 * Must be used within an InsightProvider
 */
export function useInsightContext(): InsightContextValue {
  const ctx = useContext(InsightContext);
  if (!ctx) {
    throw new Error('useInsightContext must be used within an InsightProvider');
  }
  return ctx;
}

/**
 * Calculate date range from a preset value
 */
function calculateDateRange(preset: DateRangePreset): { start: string; end: string } | undefined {
  if (preset === 'all' || preset === 'custom') {
    return undefined;
  }

  const now = new Date();
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  let start: Date;

  switch (preset) {
    case 'week':
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      break;
    case 'quarter':
      start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      break;
    case 'year':
      start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      break;
    default:
      return undefined;
  }

  return {
    start: formatDate(start),
    end: formatDate(now),
  };
}

interface InsightProviderProps {
  children: React.ReactNode;
}

/**
 * Insight context provider
 *
 * Manages shared filter state for all insight tabs.
 * Persists tenant selection in sessionStorage for SYSTEM_ADMIN users.
 */
export const InsightProvider: React.FC<InsightProviderProps> = ({ children }) => {
  const activeRole = useActiveRole();

  // Tenant selection state (persisted in sessionStorage for SYSTEM_ADMIN)
  const [selectedTenantId, setSelectedTenantIdState] = useState<string | undefined>(() => {
    if (activeRole.currentRole === UserRole.SYSTEM_ADMIN) {
      const stored = sessionStorage.getItem('insight-selected-tenant');
      return stored || undefined;
    }
    return undefined;
  });

  const setSelectedTenantId = useCallback((tenantId: string | undefined) => {
    setSelectedTenantIdState(tenantId);
    if (tenantId) {
      sessionStorage.setItem('insight-selected-tenant', tenantId);
    } else {
      sessionStorage.removeItem('insight-selected-tenant');
    }
  }, []);

  // Filter state
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('all');
  const [customDateRange, setCustomDateRange] = useState<
    { start: string; end: string } | undefined
  >(undefined);
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('week');

  // Build filters based on user's role
  const filters = useMemo<InsightFilterOptions>(() => {
    // Determine date range
    let dateRange: { start: string; end: string } | undefined;
    if (dateRangePreset === 'custom') {
      dateRange = customDateRange;
    } else {
      dateRange = calculateDateRange(dateRangePreset);
    }

    const baseFilters: InsightFilterOptions = {
      ...(dateRange && { dateRange }),
    };

    if (activeRole.currentRole === UserRole.SYSTEM_ADMIN) {
      if (selectedTenantId) {
        baseFilters.tenantId = selectedTenantId;
      }
    } else if (activeRole.currentRole === UserRole.UNIVERSITY_ADMIN) {
      if (activeRole.contextData?.tenantId) {
        baseFilters.tenantId = activeRole.contextData.tenantId;
      }
    } else if (activeRole.currentRole === UserRole.FACULTY_ADMIN) {
      if (activeRole.contextData?.domainId) {
        baseFilters.domainId = activeRole.contextData.domainId;
        baseFilters.tenantId = activeRole.contextData.tenantId;
      }
    } else if (activeRole.currentRole === UserRole.PROFESSOR) {
      if (activeRole.contextData?.subjectId) {
        baseFilters.subjectId = activeRole.contextData.subjectId;
        baseFilters.domainId = activeRole.contextData.domainId;
        baseFilters.tenantId = activeRole.contextData.tenantId;
      }
    }

    return baseFilters;
  }, [activeRole, selectedTenantId, dateRangePreset, customDateRange]);

  // Determine if queries should be enabled
  const queryEnabled = useMemo(() => {
    if (activeRole.currentRole === UserRole.SYSTEM_ADMIN) {
      return !!selectedTenantId;
    }
    if (activeRole.currentRole === UserRole.UNIVERSITY_ADMIN) {
      return !!filters.tenantId;
    }
    if (activeRole.currentRole === UserRole.FACULTY_ADMIN) {
      return !!filters.domainId && !!filters.tenantId;
    }
    if (activeRole.currentRole === UserRole.PROFESSOR) {
      return !!filters.subjectId && !!filters.tenantId;
    }
    return false;
  }, [activeRole.currentRole, filters, selectedTenantId]);

  const value = useMemo<InsightContextValue>(
    () => ({
      selectedTenantId,
      setSelectedTenantId,
      dateRangePreset,
      setDateRangePreset,
      customDateRange,
      setCustomDateRange,
      granularity,
      setGranularity,
      filters,
      queryEnabled,
      activeRole,
    }),
    [
      selectedTenantId,
      setSelectedTenantId,
      dateRangePreset,
      customDateRange,
      granularity,
      filters,
      queryEnabled,
      activeRole,
    ]
  );

  return <InsightContext.Provider value={value}>{children}</InsightContext.Provider>;
};
